import {
  Building2,
  Building2Icon,
  Contact2Icon,
  ExternalLink,
  MilestoneIcon,
  Users
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '@ui/avatar.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@r/components/ui/card.tsx';
import type { OrderOrderReadModel } from '@r/api/generated/client.schemas.ts';
import { UserFullname } from '@r/components/user-fullname.tsx';

type OrderRecipientGridProps = {
  order: OrderOrderReadModel;
  admin: boolean;
};

export function OrderRecipientGrid({ order, admin }: OrderRecipientGridProps) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
      <Card
        className={`flex flex-col gap-6 p-6 bg-muted-foreground/5 border-0 ${admin ? 'lg:col-span-2' : 'lg:col-span-3'}`}
      >
        <h1 className="flex gap-2 items-center text-xl font-bold">
          <MilestoneIcon />
          <span className="text-xl font-bold">{t('label.order.recipient.title')}</span>
        </h1>

        <div className={`grid grid-cols-1 gap-6 flex-1 ${!admin ? 'xl:grid-cols-2' : ''}`}>
          <Card className="w-full rounded-2xl">
            <CardHeader className="p-6 sm:p-10">
              <CardTitle className="flex gap-2 items-center text-xl font-bold">
                <Building2Icon />
                <span className="text-xl font-bold">
                  {t('admin.order.detail.section.delivery.address')}
                </span>
              </CardTitle>
            </CardHeader>

            <CardContent className="p-6 pt-0 sm:p-10 sm:pt-0">
              <div className="grid grid-cols-[100px_1fr] sm:grid-cols-[160px_1fr] gap-y-4 text-base min-w-0">
                <p className="text-sm font-medium text-muted-foreground">
                  {t('label.company.name')}
                </p>
                <p className="min-w-0 text-sm break-words">{order.company.name}</p>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('label.company.address.line.1')}
                </p>
                <p className="min-w-0 text-sm break-words">{order.company.addressLine1 || '-'}</p>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('label.company.address.line.2')}
                </p>
                <p className="min-w-0 text-sm break-words">{order.company.addressLine2 || '-'}</p>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('label.company.zipcode')}
                </p>
                <p className="min-w-0 text-sm break-words">{order.company.zipcode || '-'}</p>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('label.company.city')}
                </p>
                <p className="min-w-0 text-sm break-words">{order.company.city || '-'}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="w-full rounded-2xl">
            <CardHeader className="p-6 sm:p-10">
              <CardTitle className="flex gap-2 items-center text-xl font-bold">
                <Contact2Icon />
                <span className="text-xl font-bold">
                  {t('admin.order.detail.section.beneficiary')}
                </span>
              </CardTitle>
            </CardHeader>

            <CardContent className="p-6 pt-0 sm:p-10 sm:pt-0">
              <div className="grid grid-cols-[100px_1fr] sm:grid-cols-[160px_1fr] gap-y-4 text-base min-w-0">
                <p className="text-sm font-medium text-muted-foreground">
                  {t('label.user.fullname')}
                </p>
                <p className="min-w-0 text-sm break-words">
                  <UserFullname
                    fullname={order.madeBy.user.fullname}
                    isDeleted={order.madeBy.user.isDeleted}
                  />
                </p>
                <p className="text-sm font-medium text-muted-foreground">{t('label.user.email')}</p>
                <p className="min-w-0 text-sm break-words">{order.madeBy.user.email || '-'}</p>
                <p className="text-sm font-medium text-muted-foreground">{t('label.user.phone')}</p>
                <p className="min-w-0 text-sm break-words">{order.madeBy.phone || '-'}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Card>

      {admin && (
        <div className="flex flex-col gap-6 h-full min-h-0 lg:h-full">
          <Card className="flex flex-col flex-1">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="flex gap-2 items-center text-4xl font-bold">
                <Building2 />
                <span className="text-xl font-bold">{t('admin.order.detail.side.company')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 p-6 pt-0 space-y-2">
              <p className="font-semibold">{t('label.company.name')}</p>
              <div className="h-px bg-border" />
              {admin && order.company.id ? (
                <a
                  href={`/companies/${order.company.id}`}
                  className="flex justify-between items-center px-3 py-2 rounded-lg border transition-colors bg-background hover:bg-accent/40"
                >
                  <div className="flex gap-3 items-center">
                    <Avatar size="nav">
                      <AvatarFallback text={order.company.name} initials={true} />
                    </Avatar>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold">{order.company.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {order.company.typology?.name || '-'}
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </a>
              ) : (
                <div className="flex justify-between items-center px-3 py-2 rounded-lg border bg-background">
                  <div className="flex gap-3 items-center">
                    <Avatar size="nav">
                      <AvatarFallback text={order.company.name} initials={true} />
                    </Avatar>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold">{order.company.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {order.company.typology?.name || '-'}
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="flex flex-col flex-1">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="flex gap-2 items-center text-4xl font-bold">
                <Users />
                <span className="text-xl font-bold">{t('admin.order.detail.side.contacts')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 p-6 pt-0 space-y-2">
              <p className="font-semibold">{t('admin.order.detail.side.collaborators')}</p>
              <div className="h-px bg-border" />
              {admin && order.madeBy.user.id ? (
                <a
                  href={`/users/${order.madeBy.user.id}`}
                  className="flex justify-between items-center px-3 py-2 rounded-lg border transition-colors bg-background hover:bg-accent/40"
                >
                  <div className="flex gap-3 items-center">
                    <Avatar size="nav">
                      <AvatarFallback text={order.madeBy.user.fullname} initials={true} />
                    </Avatar>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold">
                        <UserFullname
                          fullname={order.madeBy.user.fullname}
                          isDeleted={order.madeBy.user.isDeleted}
                        />
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {order.madeBy.user.email || '-'}
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </a>
              ) : (
                <div className="flex justify-between items-center px-3 py-2 rounded-lg border bg-background">
                  <div className="flex gap-3 items-center">
                    <Avatar size="nav">
                      <AvatarFallback text={order.madeBy.user.fullname} initials={true} />
                    </Avatar>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold">
                        <UserFullname
                          fullname={order.madeBy.user.fullname}
                          isDeleted={order.madeBy.user.isDeleted}
                        />
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {order.madeBy.user.email || '-'}
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
              )}

              {order.company.responsibles?.length ? (
                <>
                  <p className="pt-2 font-semibold">{t('admin.order.detail.side.sales.contact')}</p>
                  <div className="h-px bg-border" />
                  <div className="space-y-2">
                    {order.company.responsibles.map((responsible, index) => {
                      const responsibleUser = responsible.user;
                      const content = (
                        <>
                          <div className="flex gap-3 items-center">
                            <Avatar size="nav">
                              <AvatarFallback text={responsibleUser.fullname} initials={true} />
                            </Avatar>
                            <div className="leading-tight">
                              <p className="text-sm font-semibold">
                                <UserFullname
                                  fullname={responsibleUser.fullname}
                                  isDeleted={responsibleUser.isDeleted}
                                />
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {t('admin.user.profile.contacts.commercial.responsible')}
                              </p>
                            </div>
                          </div>
                          {admin && responsibleUser.id ? (
                            <ExternalLink className="w-4 h-4 text-muted-foreground" />
                          ) : null}
                        </>
                      );

                      return admin && responsibleUser.id ? (
                        <a
                          key={`${responsibleUser.id}-${index}`}
                          href={`/users/${responsibleUser.id}`}
                          className="flex justify-between items-center px-3 py-2 rounded-lg border transition-colors bg-background hover:bg-accent/40"
                        >
                          {content}
                        </a>
                      ) : (
                        <div
                          key={`${responsibleUser.fullname}-${index}`}
                          className="flex justify-between items-center px-3 py-2 rounded-lg border bg-background"
                        >
                          {content}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
